/**
 * <header-nav-panels>
 *
 * Three-panel dropdown. Hovering or focusing a group reveals its items;
 * hovering or focusing an item reveals its children. The first group and item
 * are marked active in the markup, so the panel renders without JavaScript.
 */
(function () {
  const SELECTORS = {
    trigger: '[data-panel-trigger]',
    topLink: '[data-top-link]',
    actionLink: '.nav-panels__leaf, .nav-panels__all, .nav-panels__view-all',
    headerDropdown: '.header__dropdown',
    disclosure: 'hover-disclosure',
  };

  const CLASSES = {
    active: 'is-active',
    visible: 'is-visible',
  };

  const PANEL_GROUP = 'group';
  const PANEL_ITEM = 'item';
  const TOP_LINK_TOUCH_BOUND = '__btgHeaderTopLinkTouchBound';
  const TOP_LINK_PRESS_OPTIONS = { capture: true };
  const TOP_LINK_TOUCH_OPTIONS = { passive: false, capture: true };

  let lastTopLinkPointerType = '';
  let skipNextTopLinkClick = false;
  let lastTopLinkHandledAt = 0;

  function usesTouchNavigation() {
    return window.matchMedia('(hover: none), (pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  }

  function isMousePress(event, lastPointerType) {
    if (event.pointerType) return event.pointerType === 'mouse';
    return event.type === 'click' && lastPointerType === 'mouse';
  }

  function onTopLinkPress(event) {
    if (event.type === 'pointerdown') {
      lastTopLinkPointerType = event.pointerType || '';
    }

    if (!usesTouchNavigation() || isMousePress(event, lastTopLinkPointerType)) {
      if (event.type === 'click') lastTopLinkPointerType = '';
      return;
    }

    const topLink = event.target.closest(SELECTORS.topLink);
    if (!topLink) return;

    const disclosure = topLink.closest(SELECTORS.disclosure);
    if (!disclosure || !disclosure.querySelector(SELECTORS.headerDropdown)) return;

    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'click' && skipNextTopLinkClick) {
      skipNextTopLinkClick = false;
      lastTopLinkPointerType = '';
      return;
    }

    if (event.type === 'pointerdown' || event.type === 'touchstart') {
      const now = Date.now();
      if (now - lastTopLinkHandledAt < 400) return;
      lastTopLinkHandledAt = now;
      skipNextTopLinkClick = true;
    }

    const eventName = disclosure.classList.contains(CLASSES.visible) ? 'theme:disclosure:hide' : 'theme:disclosure:show';
    disclosure.dispatchEvent(new CustomEvent(eventName, { bubbles: false }));
  }

  if (!window[TOP_LINK_TOUCH_BOUND]) {
    document.addEventListener('pointerdown', onTopLinkPress, TOP_LINK_PRESS_OPTIONS);
    document.addEventListener('touchstart', onTopLinkPress, TOP_LINK_TOUCH_OPTIONS);
    document.addEventListener('click', onTopLinkPress, TOP_LINK_PRESS_OPTIONS);
    window[TOP_LINK_TOUCH_BOUND] = true;
  }

  if (customElements.get('header-nav-panels')) return;

  customElements.define(
    'header-nav-panels',
    class HeaderNavPanels extends HTMLElement {
      constructor() {
        super();

        this.onTriggerActivate = this.onTriggerActivate.bind(this);
        this.onNavigationPress = this.onNavigationPress.bind(this);
        this.reset = this.reset.bind(this);
      }

      connectedCallback() {
        this.disclosure = this.closest(SELECTORS.disclosure);

        this.addEventListener('mouseover', this.onTriggerActivate);
        this.addEventListener('focusin', this.onTriggerActivate);

        if (this.disclosure) {
          this.disclosure.addEventListener('mouseleave', this.reset);
          this.disclosure.addEventListener('pointerdown', this.onNavigationPress);
          this.disclosure.addEventListener('touchstart', this.onNavigationPress, { passive: false });
          this.disclosure.addEventListener('click', this.onNavigationPress);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('mouseover', this.onTriggerActivate);
        this.removeEventListener('focusin', this.onTriggerActivate);

        if (this.disclosure) {
          this.disclosure.removeEventListener('mouseleave', this.reset);
          this.disclosure.removeEventListener('pointerdown', this.onNavigationPress);
          this.disclosure.removeEventListener('touchstart', this.onNavigationPress);
          this.disclosure.removeEventListener('click', this.onNavigationPress);
        }
      }

      onTriggerActivate(event) {
        const trigger = event.target.closest(SELECTORS.trigger);
        if (!trigger || !this.contains(trigger)) return;

        this.activate(trigger.dataset.panelTrigger, trigger.dataset.panelTarget);
      }

      onNavigationPress(event) {
        if (!this.usesTouchNavigation()) return;
        if (event.pointerType && event.pointerType === 'mouse') return;
        if (event.target.closest(SELECTORS.actionLink)) return;

        const trigger = event.target.closest(SELECTORS.trigger);
        if (trigger && this.contains(trigger)) {
          event.preventDefault();
          this.activate(trigger.dataset.panelTrigger, trigger.dataset.panelTarget);
        }
      }

      usesTouchNavigation() {
        return usesTouchNavigation();
      }

      /**
       * @param {string} type - `group` or `item`.
       * @param {string} id - Value of the matching `data-panel-id`.
       */
      activate(type, id) {
        if (!type || !id) return;

        this.querySelectorAll(`[data-panel-trigger="${type}"]`).forEach((trigger) => {
          trigger.classList.toggle(CLASSES.active, trigger.dataset.panelTarget === id);
        });

        let activePanel = null;

        this.querySelectorAll(`[data-panel="${type}"]`).forEach((panel) => {
          const isActive = panel.dataset.panelId === id;
          panel.classList.toggle(CLASSES.active, isActive);

          if (isActive) activePanel = panel;
        });

        if (type === PANEL_GROUP && activePanel) {
          this.activateFirst(PANEL_ITEM, activePanel);
        }
      }

      /**
       * @param {string} type - `group` or `item`.
       * @param {ParentNode} [scope] - Element to search within, defaults to this element.
       */
      activateFirst(type, scope) {
        const trigger = (scope || this).querySelector(`[data-panel-trigger="${type}"]`);
        if (!trigger) return;

        this.activate(type, trigger.dataset.panelTarget);
      }

      /** Returns the dropdown to its initial state so it always opens on the first group. */
      reset() {
        this.activateFirst(PANEL_GROUP);
      }
    }
  );
})();
